import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import { api } from "../lib/api";

const day = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

/** Every site the public has filed, anonymised. Momentum you can look at, not just count. */
export const FiledSites = ({ limit = 15 }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/apply/map").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  if (!data) return null;

  return (
    <section className="mt-20" data-testid="filed-sites">
      <p className="mono text-[0.55rem] uppercase tracking-[0.2em] text-ochre">The pin map</p>
      <h2 className="mt-4 text-base font-semibold text-white md:text-lg">{data.heading}</h2>
      <p className="body-copy mt-4 max-w-3xl">{data.intro}</p>

      <div className="mt-8 grid gap-px bg-zinc-800 sm:grid-cols-3" data-testid="filed-sites-stats">
        {[
          ["Sites filed", data.total],
          ["Counties with a pin", `${data.counties_pinned} of 47`],
          ["Counties still open", 47 - data.counties_pinned],
        ].map(([k, v], i) => (
          <div key={k} className="bg-[#0b0b0b] p-6" data-testid={`filed-sites-stat-${i}`}>
            <p className="mono text-[0.5rem] uppercase tracking-[0.18em] text-zinc-600">{k}</p>
            <p className="mono mt-3 text-base text-white md:text-lg">{v}</p>
          </div>
        ))}
      </div>

      {data.total === 0 ? (
        <p className="mono mt-8 max-w-2xl border-l-2 border-ochre bg-[#0b0b0b] p-6 text-[0.62rem] uppercase leading-relaxed tracking-[0.14em] text-ochre" data-testid="filed-sites-empty">
          No site has been pinned yet. The first one goes on the map the day you file it.
        </p>
      ) : (
        <div className="mt-8 space-y-px bg-zinc-800" data-testid="filed-sites-list">
          {data.pins.slice(0, limit).map((p, i) => (
            <div key={`${p.county}-${p.filed_at}-${i}`} className="flex flex-wrap items-baseline justify-between gap-3 bg-[#0b0b0b] px-5 py-4 md:px-7" data-testid={`filed-site-${i}`}>
              <div className="flex items-start gap-3">
                <MapPin size={13} className="mt-1 shrink-0 text-ochre" />
                <div>
                  <p className="text-sm text-white">
                    {p.county}
                    {p.ward ? <span className="text-zinc-500"> · {p.ward}</span> : null}
                  </p>
                  <p className="mt-1 text-xs text-zinc-500">
                    {p.proposal}
                    {p.acres ? ` · ${p.acres} acres` : ""}
                    {p.tenure ? ` · ${p.tenure}` : ""}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="mono text-[0.55rem] uppercase tracking-[0.14em] text-ochre">{p.kind}</p>
                <p className="mono mt-1 text-[0.55rem] uppercase tracking-[0.14em] text-zinc-500">{day(p.filed_at)}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      <Link to="/apply" className="btn-primary mt-8 inline-flex items-center gap-2" data-testid="filed-sites-apply">
        Pin a site in your county <ArrowRight size={14} />
      </Link>
    </section>
  );
};

export default FiledSites;
